import { IconName } from "@/lib/modules/icon-mapper";
import { FormField } from "./FormField";
import DynamicIcon from "@/components/utils/lucide-icon";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";

type SelectOption = {
  value: string;
  label: string;
  icon?: IconName;
};

type SelectFieldProps = {
  label: string;
  name: string;
  options: SelectOption[];
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  icon?: IconName;
  tooltip?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  value?: string;
  hidden?: boolean;
  className?: string;
};

export function SelectField({
  label,
  name,
  options,
  placeholder = "Selecciona una opción",
  required = false,
  helperText,
  error,
  icon,
  tooltip,
  disabled = false,
  value: controlledValue,
  onValueChange: controlledOnValueChange,
  defaultValue,
  hidden = false,
  className,
}: SelectFieldProps) {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue || "");

  const currentValue = isControlled ? controlledValue : internalValue;

  const handleValueChange = (value: string) => {
    if (isControlled && controlledOnValueChange) {
      controlledOnValueChange(value);
    } else if (!isControlled) {
      setInternalValue(value);
    }
  };

  return (
    <FormField
      label={label}
      htmlFor={name}
      required={required}
      helperText={helperText}
      error={error}
      icon={icon}
      tooltip={tooltip}
      hidden={hidden}
    >
      <Select
        value={currentValue}
        onValueChange={handleValueChange}
        disabled={disabled}
        required={required}
      >
        <SelectTrigger id={name} onBlur={(e) => e.preventDefault()}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className={className}>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.icon && (
                <DynamicIcon name={option.icon} className="mr-2 h-4 w-4" />
              )}
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <input type="hidden" name={name} value={currentValue} />
    </FormField>
  );
}
